import { useState, useEffect } from 'react';
import { collection, query, limit, onSnapshot, where, orderBy } from 'firebase/firestore';
import { db } from '../../../firebase';
import { tsToLocal } from '../ticketUtils';
import { QUERY_LIMITS, truncationInfo, NO_TRUNCATION } from '../lib/queryLimits';

const LIMIT = QUERY_LIMITS.ticketsInRange;

/**
 * Reads ticketChecks records within a date range.
 * Used by Analytics / All Tickets / Category Sales tabs.
 *
 * Also returns `truncation` so those tabs can warn when the range overflowed
 * the cap rather than computing KPIs from a partial slice.
 */
export function useFirestoreTicketsInRange(startDt, endDt, enabled) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [truncation, setTruncation] = useState(NO_TRUNCATION);

  useEffect(() => {
    if (!enabled) return;
    setLoading(true);
    setError(null);
    const q = query(
      collection(db, 'ticketChecks'),
      where('updatedAt', '>=', startDt),
      where('updatedAt', '<=', endDt),
      orderBy('updatedAt', 'desc'),
      limit(LIMIT),
    );
    const unsub = onSnapshot(q, snap => {
      const raw = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      const filtered = raw.filter(r => {
        const dt =
          (r.createdAtISO ? new Date(r.createdAtISO) : null) ||
          (r.completedAt ? tsToLocal(r.completedAt) : null) ||
          (r.updatedAt ? tsToLocal(r.updatedAt) : null);
        return dt && dt >= startDt && dt <= endDt;
      });
      // Measured against the RAW size — Firestore applies the cap before this
      // client-side re-filter, so a small `filtered` count can still be capped.
      setTruncation(truncationInfo({ fetched: snap.size, limit: LIMIT, kept: filtered.length }));
      setRecords(filtered);
      setLoading(false);
      setError(null);
    }, e => { setError(e.message); setLoading(false); });
    return () => unsub();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDt.getTime(), endDt.getTime(), enabled]);

  return { records, loading, error, truncation };
}
